import angular from 'angular';

// This service debounces calls from installations-table-chart components into a one per installation

/**
 * @typedef {Object} QueuedInstallationFetch
 * @property {number} idSite
 * @property {Promise<any>} deferred
 * @property {Array<string>} attributes
 */

/**
 * Cache to prevent refetching data when user quickly scrolls or rows reorder.
 * Keyed by idSite
 * @type {Map<any, {exp:number, data:array<any>,signature:string}>}
 */
const dataCache = new Map();
const dataCacheTtl = 1000 * 60 * 15;

/**
 * Performs a lookup in the cache
 * @param {string} key - The key to store the value under
 * @param {string} signature - String that validates the stored value against the current context, allows for invalidation without storing for new keys
 * @returns {array<*>}
 */
function cachedLookup(key, signature = key) {
    const now = (new Date()).getTime();
    const entry = dataCache.get(key);
    if (!entry || entry.exp < now || entry.signature !== signature) {
        return null;
    }
    return entry.data;
}

function cachePut(key, data, signature = key) {
    const now = (new Date()).getTime();
    const entry = {
        exp: now + dataCacheTtl,
        signature,
        data,
    };
    dataCache.set(key, entry);
}

// Purges the cache every once in a while
function cleanupCache() {
    const now = (new Date()).getTime();
    dataCache.forEach((entry, key) => {
        if (entry.exp < now) {
            dataCache.delete(key);
        }
    });
}
setInterval(cleanupCache, dataCacheTtl / 2);

class Deferred {
    promise;
    resolve;
    reject;

    constructor() {
        this.promise = new Promise((resolve, reject) => {
            this.resolve = resolve;
            this.reject = reject;
        });
    }
}

class ChartLoaderService {
    debounceTimeoutMillis = 1000;
    debouncer = null; // setTimeout() for a task that periodically kicks off the requests
    liveGraphRange = 60 * 60 * 24;
    liveGraphStepSize = 60 * 15;

    /** @type {Map<number, QueuedInstallationFetch>} */
    queue = new Map();

    /* @ngInject */
    constructor($timeout, $q, GraphService, ApiService) {
        this.$timeout = $timeout;
        this.$q = $q;
        this.GraphService = GraphService;
        this.ApiService = ApiService;
    }

    getGraphStart() {
        return this.getGraphEnd() - this.liveGraphRange;
    }

    getGraphEnd() {
        const now = Math.floor((new Date().getTime()) / 1000);
        // Round down to the previous step
        const interval = this.liveGraphStepSize;
        return Math.floor(now / interval) * interval;
    }

    queueGraphDataLoad(idSite, attribute) {
        if (!this.queue.has(idSite)) {
            const item = {
                idSite,
                attributes: [],
                deferred: new Deferred(),
                checked: false,
            };
            this.queue.set(idSite, item);
            this.ensureQueueRunner();
        }
        const queued = this.queue.get(idSite);
        if (!queued.attributes.includes(attribute)) {
            queued.attributes.push(attribute);
        }
        queued.checked = false;

        // Attempt resolving from cache
        this.resolveUsingCache(queued);

        return queued.deferred.promise;
    }

    releaseDataLoad(idSite, attribute) {
        const queued = this.queue.get(idSite);
        if (!queued) return;
        const index = queued.attributes.indexOf(attribute);
        if (index >= 0) {
            if (queued.attributes.length > 1) {
                queued.attributes.splice(index, 1);
            } else {
                // Request is cancelled if the last attribute is released
                this.queue.delete(idSite);
            }
        }
    }

    /**
     * @param {number[]} idSites
     */
    executeFetch(idSites) {
        if (!idSites.length) return;
        const allAttributes = new Set();
        const allDefers = [];
        idSites.forEach(idSite => {
            const {
                attributes,
                deferred,
            } = this.queue.get(idSite);
            this.queue.delete(idSite);
            attributes.forEach(x => allAttributes.add(x));
            deferred.__idSite = idSite;
            deferred.__cacheKey = this.makeCacheKey({ idSite, attributes, deferred});
            allDefers.push(deferred);
        });

        const attributeCodes = Array.from(allAttributes.values());

        const payload = {
            id: idSites.join(','),
            start: this.getGraphStart(),
            end: this.getGraphEnd(),
            type: 'custom',
            interval: 'hours',
            attributeCodes,
        };

        const apiPromise = this.ApiService.call2(`/installations/overview-stats`, payload);

        apiPromise.then(
            result => {
                allDefers.forEach(defer => {
                    const idSite = defer.__idSite;
                    const data = result.data.result[idSite];
                    defer.resolve(data);
                    cachePut(defer.__cacheKey, data);
                });
            },
            error => {
                allDefers.forEach(defer => defer.reject(error));
            }
        );
    }

    parseQueue() {
        if (this.debouncer) {
            clearTimeout(this.debouncer);
            this.debouncer = null;
        }
        let hasUnchecked = false;
        const fetchable = [];
        this.queue.forEach((item, idSite) => {
            if (item.checked) {
                fetchable.push(idSite);
            } else {
                hasUnchecked = (item.checked = true);
            }
        });
        if (hasUnchecked) {
            this.ensureQueueRunner();
        }
        this.executeFetch(fetchable);
    }

    ensureQueueRunner() {
        if (!this.debouncer) {
            this.debouncer = setTimeout(() => this.parseQueue(), this.debounceTimeoutMillis);
        }
    }

    resolveUsingCache(item) {
        const cacheValue = cachedLookup(this.makeCacheKey(item))
        if (cacheValue === null) {
            return false;
        }
        item.deferred.resolve(cacheValue);
        this.queue.delete(item.idSite)
        return true;
    }

    makeCacheKey(item) {
        // For simplicity, we store by site. It would be better to do so by site and attribute,
        // if the columns are changed.
        return `${item.idSite}:${item.attributes.join('_')}`;
    }
}

angular.module('vrm.components').service('ChartLoaderService', ChartLoaderService);
